/**
 * features/combo/model/useComboBuilder
 *
 * Модель конструктора комбо (карточки-слоты + «Заменить» + «Изменить состав»).
 *
 * Отвечает за:
 *  — слоты по группам комбо и выбранный товар в каждом;
 *  — группировку одноимённых товаров в варианты (25/30/35 см и т.п.);
 *  — дельты цен относительно текущего выбора («+50 ₽» на карточках замены);
 *  — добавки/отбавки слота и их влияние на итоговую цену;
 *  — payload для корзины (selections + modifiers).
 *
 * Цена здесь — предварительная: источник истины остаётся на бэкенде,
 * он же валидирует допустимость добавок для товара.
 */

import { computed, reactive, ref, watch, type ComputedRef } from 'vue';
import { consola } from 'consola';
import apiProductInstance, { isMenuItemAvailable } from '~/entities/product';
import type {
  ComboCustomizations,
  ComboGroup,
  ComboGroupItem,
  ComboProduct,
  ComboSelections,
  ProductDetailResponse,
} from '@entities/product';

const logger = consola.withTag('useComboBuilder');

/**
 * Добавки/отбавки слота с готовым URL картинки.
 *
 * `img` в ответах каталога — массив `[{key, url}]`, но встречаются и объект,
 * и голая строка. Нормализуем здесь, чтобы UI не разбирался в форматах.
 */
export interface ComboSlotAddon {
  id: number;
  name: string;
  price: number;
  img: string | null;
}

export interface ComboSlotExclude {
  id: number;
  name: string;
  img: string | null;
}

/** Достаёт URL картинки из любого формата поля img */
const resolveImageUrl = (img: unknown): string | null => {
  const source = Array.isArray(img) ? img[0] : img;
  if (!source) return null;
  if (typeof source === 'string') return source;
  const url = (source as { url?: string }).url;
  return url ?? null;
};

/** Группа одноимённых товаров слота — «одно блюдо в разных размерах» */
export interface ComboVariantFamily {
  /** Ключ семейства (нормализованное название) */
  key: string;
  name: string;
  /** Товары семейства по возрастанию цены */
  products: ComboGroupItem[];
  /** Самый дешёвый ДОСТУПНЫЙ товар — по нему считается дельта на карточке замены */
  cheapest: ComboGroupItem;
  /** false — ни один размер сейчас не доступен: карточку нельзя выбрать */
  isAvailable: boolean;
}

/** Слот конструктора: одна группа комбо */
export interface ComboSlot {
  group: ComboGroup;
  /** Выбранный товар (null, если группа пуста) */
  product: ComboGroupItem | null;
  /** Семейства для сетки «Заменить» */
  families: ComboVariantFamily[];
  /** Варианты выбранного товара (чипсы размеров); пусто, если вариант один */
  variants: ComboGroupItem[];
  /** Добавки/отбавки выбранного товара (подгружаются лениво) */
  addons: ComboSlotAddon[];
  excludes: ComboSlotExclude[];
  /** Выбранные добавки/отбавки */
  selectedAddons: number[];
  selectedExcludes: number[];
  /** Сумма выбранных добавок */
  addonsPrice: number;
  /** Есть ли что менять в составе */
  canCustomize: boolean;
}

export interface ComboCartSelection {
  group_id: number;
  product_ids: number[];
  modifiers?: Array<{ product_id: number; addons: number[]; excludes: number[] }>;
}

export interface UseComboBuilderOptions {
  combo: ComputedRef<ComboProduct | null>;
  branchId: ComputedRef<string | null>;
}

/** Ключ семейства вариантов: одноимённые товары группы = один и тот же продукт в разных размерах */
const familyKey = (product: ComboGroupItem): string => product.name.trim().toLowerCase();

export function useComboBuilder({ combo, branchId }: UseComboBuilderOptions) {
  /** { groupId: productId } */
  const selections = ref<ComboSelections>({});
  /** { groupId: { addons, excludes } } */
  const customizations = reactive<ComboCustomizations>({});
  /** Кэш карточек товаров: добавки/отбавки/состав. Ключ — productId */
  const details = reactive<Record<number, ProductDetailResponse>>({});
  const loadingDetailFor = ref<number | null>(null);

  // ─── Инициализация ──────────────────────────────────────────────────────────

  const initializeDefaults = () => {
    const next: ComboSelections = {};

    for (const group of combo.value?.groups ?? []) {
      // Недоступный дефолт не подставляем: комбо открылось бы на товаре, который
      // нельзя добавить, и упёрлось в заблокированную кнопку.
      const defaultProduct =
        group.products.find((p) => p.is_default && isMenuItemAvailable(p)) ??
        group.products.find(isMenuItemAvailable) ??
        group.products.find((p) => p.is_default) ??
        group.products[0];
      if (defaultProduct) next[group.id] = defaultProduct.id;
    }

    selections.value = next;
    for (const key of Object.keys(customizations)) delete customizations[Number(key)];
  };

  watch(combo, initializeDefaults, { immediate: true });

  // ─── Данные слотов ──────────────────────────────────────────────────────────

  const getProduct = (group: ComboGroup): ComboGroupItem | null =>
    group.products.find((p) => p.id === selections.value[group.id]) ?? null;

  const buildFamilies = (group: ComboGroup): ComboVariantFamily[] => {
    const byKey = new Map<string, ComboGroupItem[]>();

    for (const product of [...group.products].sort((a, b) => a.sort_order - b.sort_order)) {
      const key = familyKey(product);
      byKey.set(key, [...(byKey.get(key) ?? []), product]);
    }

    return [...byKey.entries()].map(([key, products]) => {
      const sorted = [...products].sort((a, b) => a.price - b.price);
      // Семейство доступно, пока жив хоть один размер: «Кола 0.5» может кончиться,
      // а «Кола 0.33» — нет, и карточку в этом случае гасить нельзя.
      const available = sorted.filter(isMenuItemAvailable);
      return {
        key,
        name: sorted[0].name,
        products: sorted,
        cheapest: available[0] ?? sorted[0],
        isAvailable: available.length > 0,
      };
    });
  };

  const slots = computed<ComboSlot[]>(() =>
    (combo.value?.groups ?? []).map((group) => {
      const product = getProduct(group);
      const families = buildFamilies(group);
      const family = product ? families.find((f) => f.key === familyKey(product)) : undefined;
      const detail = product ? details[product.id] : undefined;
      const customization = customizations[group.id];
      const selectedAddons = customization?.addons ?? [];
      const selectedExcludes = customization?.excludes ?? [];

      const addons: ComboSlotAddon[] = (detail?.addons ?? []).map((addon) => ({
        id: addon.id,
        name: addon.name,
        price: addon.price,
        img: resolveImageUrl(addon.img),
      }));

      const excludes: ComboSlotExclude[] = (detail?.excludes ?? []).map((exclude) => ({
        id: exclude.id,
        name: exclude.name,
        img: resolveImageUrl(exclude.img),
      }));

      return {
        group,
        product,
        families,
        variants: (family?.products.length ?? 0) > 1 ? family!.products : [],
        addons,
        excludes,
        selectedAddons,
        selectedExcludes,
        addonsPrice: selectedAddons.reduce(
          (sum, id) => sum + (addons.find((a) => a.id === id)?.price ?? 0),
          0,
        ),
        canCustomize: addons.length > 0 || excludes.length > 0,
      };
    }),
  );

  /** Дельта цены товара относительно выбранного сейчас в этом слоте */
  const getPriceDelta = (group: ComboGroup, product: ComboGroupItem): number => {
    const current = getProduct(group);
    return Math.round((product.price - (current?.price ?? 0)) * 100) / 100;
  };

  // ─── Цена ───────────────────────────────────────────────────────────────────

  const calculatedPrice = computed(() => {
    if (!combo.value) return { basePrice: 0, discountAmount: 0, finalPrice: 0 };

    let basePrice = 0;
    let discountablePrice = 0;
    let addonsPrice = 0;

    for (const slot of slots.value) {
      if (!slot.product) continue;
      basePrice += slot.product.price;
      if (slot.group.apply_discount) discountablePrice += slot.product.price;
      addonsPrice += slot.addonsPrice;
    }

    const discountAmount = (discountablePrice * combo.value.discount_percent) / 100;
    // Добавки идут по полной цене — скидка комбо на них не распространяется (как на бэке)
    const finalPrice = Math.round((basePrice - discountAmount + addonsPrice) * 100) / 100;

    return { basePrice: basePrice + addonsPrice, discountAmount, finalPrice };
  });

  /** Выбранный в слоте товар недоступен (состав мог протухнуть, пока комбо открыто) */
  const isSelectionUnavailable = (group: ComboGroup): boolean => {
    const productId = selections.value[group.id];
    if (!productId) return false;
    const product = group.products.find((p) => p.id === productId);
    return !!product && !isMenuItemAvailable(product);
  };

  /**
   * В обязательной группе нечем закрыть слот: список товаров пуст ИЛИ все на стопе.
   *
   * Пустой список — не редкость при мультифилиальности: товары группы просто не заведены
   * на этом филиале. Раньше такая группа считалась выполненной, и комбо уезжало в корзину
   * без обязательной позиции по полной цене.
   */
  const groupHasNoAvailableProducts = (group: ComboGroup): boolean =>
    group.is_required && !group.products?.some(isMenuItemAvailable);

  const isValid = computed(() =>
    (combo.value?.groups ?? []).every((group) => {
      if (isSelectionUnavailable(group) || groupHasNoAvailableProducts(group)) return false;
      if (!group.is_required) return true;
      return !!selections.value[group.id];
    }),
  );

  /** Обязательная группа, которую нечем закрыть — комбо не собрать вообще */
  const blockedGroup = computed<ComboGroup | null>(
    () => (combo.value?.groups ?? []).find(groupHasNoAvailableProducts) ?? null,
  );

  // ─── Действия ───────────────────────────────────────────────────────────────

  /**
   * Выбор товара в слоте. Кастомизация сбрасывается: добавки принадлежат
   * конкретному товару, переносить их на другой нельзя.
   */
  const selectProduct = (groupId: number, productId: number): boolean => {
    const group = combo.value?.groups.find((g) => g.id === groupId);
    const product = group?.products.find((p) => p.id === productId);

    // Недоступный товар не выбираем и сообщаем вызывающему — он покажет тост.
    if (product && !isMenuItemAvailable(product)) return false;

    if (selections.value[groupId] === productId) return true;
    selections.value = { ...selections.value, [groupId]: productId };
    delete customizations[groupId];
    void loadSlotDetail(groupId);
    return true;
  };

  /** Полный набор добавок слота — компоненты модификаторов отдают массив целиком */
  const setSlotAddons = (groupId: number, addonIds: number[]) => {
    const current = customizations[groupId] ?? { addons: [], excludes: [] };
    customizations[groupId] = { ...current, addons: [...addonIds] };
  };

  const setSlotExcludes = (groupId: number, excludeIds: number[]) => {
    const current = customizations[groupId] ?? { addons: [], excludes: [] };
    customizations[groupId] = { ...current, excludes: [...excludeIds] };
  };

  const resetSlotCustomization = (groupId: number) => {
    delete customizations[groupId];
  };

  /** Подгружает состав/добавки товара слота (кэш по productId) */
  const loadSlotDetail = async (groupId: number) => {
    const productId = selections.value[groupId];
    if (!productId || details[productId] || !branchId.value) return;

    loadingDetailFor.value = productId;
    try {
      details[productId] = await apiProductInstance.getProductDetail(
        Number(productId),
        Number(branchId.value),
      );
    } catch (error) {
      logger.warn('Не удалось загрузить состав товара комбо', { productId, error });
    } finally {
      if (loadingDetailFor.value === productId) loadingDetailFor.value = null;
    }
  };

  /**
   * Прогрев: состав выбранных товаров нужен сразу — карточка слота показывает его без задержки.
   * Ждём филиал: модалка часто монтируется раньше, чем резолвится selectedBranch,
   * а без branchId запрос состава не уйдёт и «Изменить состав» не появится вовсе.
   */
  const preloadSelectedDetails = async () => {
    await Promise.all((combo.value?.groups ?? []).map((group) => loadSlotDetail(group.id)));
  };

  watch(
    [combo, branchId],
    ([nextCombo, nextBranchId]) => {
      if (nextCombo && nextBranchId) void preloadSelectedDetails();
    },
    { immediate: true },
  );

  // ─── Корзина ────────────────────────────────────────────────────────────────

  /** selections в формате API: группы + модификаторы слотов */
  const cartSelections = computed<ComboCartSelection[]>(() =>
    slots.value
      .filter((slot) => !!slot.product)
      .map((slot) => {
        const selection: ComboCartSelection = {
          group_id: slot.group.id,
          product_ids: [slot.product!.id],
        };

        if (slot.selectedAddons.length > 0 || slot.selectedExcludes.length > 0) {
          selection.modifiers = [
            {
              product_id: slot.product!.id,
              addons: [...slot.selectedAddons],
              excludes: [...slot.selectedExcludes],
            },
          ];
        }

        return selection;
      }),
  );

  const getCartData = () => {
    if (!combo.value) return null;

    return {
      type: 'combo' as const,
      combo_id: combo.value.id,
      name: combo.value.name,
      img: (combo.value.img ?? []).map((img: any) => img?.url ?? img),
      price: calculatedPrice.value.finalPrice,
      base_price: calculatedPrice.value.basePrice,
      discount_percent: combo.value.discount_percent,
      discount_amount: calculatedPrice.value.discountAmount,
      selections: cartSelections.value,
    };
  };

  return {
    // state
    selections,
    customizations,
    loadingDetailFor,

    // computed
    slots,
    calculatedPrice,
    isValid,
    blockedGroup,
    cartSelections,

    // methods
    getPriceDelta,
    selectProduct,
    setSlotAddons,
    setSlotExcludes,
    resetSlotCustomization,
    loadSlotDetail,
    preloadSelectedDetails,
    getCartData,
    initializeDefaults,
  };
}
